import { useForm } from '@mantine/form';
import { FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import { isEmpty, noop } from 'lodash';
import FormWrapper from 'components/forms/FormWrapper';
import { Alert, Divider, Flex, Group, Image, Modal, SimpleGrid, Stack, TextInput } from '@mantine/core';
import Button from 'components/Button';
import { IconCheck, IconPlus, IconTrashX } from '@tabler/icons-react';
import FormInputGroup from 'components/forms/FormInputGroup';
import UploadFile from 'components/files/UploadFile';
import { nanoid } from 'nanoid';
import { useCallback, useState } from 'react';
import DeviceTypeLabel from 'components/device/DeviceTypeLabel';
import { usePickDeviceType } from 'components/modals/pick-device-type/PickDeviceTypeProvider';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import H1Medium from 'components/typography/H1Medium';
import { DateInput } from 'components/inputs/DateInput';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * The data collected from the form.
 */
export interface MyCertificatesFormData {
  certificates: {
    certificateId?: number;
    type: string;
    number: string;
    renewalDate: string;
    certificate: string;
    stamp: string;
    paired: boolean;
    termVersion?: number;
    nextDueDate?: string;
    deadlineState?: string;
  }[];
}

interface RequalificationData {
  executedOn: string;
  certificateNumber: string;
  certificateFileId: string;
  stampFileId: string;
}

interface MyCertificatesFormProps extends FormProps<MyCertificatesFormData> {
  onRequalify?: (
    certificate: MyCertificatesFormData['certificates'][number],
    data: RequalificationData
  ) => Promise<any>;
}

/**
 * Form for editing user's own certificates.
 */
export default function MyCertificatesForm({
  initialValues = {
    certificates: [],
  },
  onSubmit = noop,
  onRequalify,
}: MyCertificatesFormProps) {
  const { pickDeviceType } = usePickDeviceType();
  const { confirm } = useConfirm();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const { isMobile, isTablet } = useResponsiveDesign();

  const [extendedInitialValues] = useState(() => ({
    ...initialValues,
    certificates: initialValues.certificates.map((certificate) => ({ ...certificate, uuid: nanoid() })),
  }));
  const [requalifyIndex, setRequalifyIndex] = useState<number | null>(null);
  const [requalifying, setRequalifying] = useState(false);

  const requalificationForm = useForm<RequalificationData>({
    initialValues: {
      executedOn: '',
      certificateNumber: '',
      certificateFileId: '',
      stampFileId: '',
    },
    validate: {
      executedOn: (value) =>
        !value ? 'Pole je povinné' : new Date(value) > new Date() ? 'Dátum nesmie byť v budúcnosti' : null,
      certificateNumber: createValidator([required]),
      certificateFileId: createValidator([required]),
      stampFileId: createValidator([required]),
    },
  });

  const form = useForm({
    initialValues: extendedInitialValues,
    validate: {
      certificates: {
        number: createValidator([required]),
        renewalDate: (date) => (date ? null : 'Pole je povinné'),
        certificate: createValidator([required]),
        stamp: createValidator([required]),
      },
    },
  });

  useDirtyFormHandler(form);

  /**
   * Adds a new qualification to the form.
   */
  const addCertificate = useCallback(() => {
    pickDeviceType({
      title: 'Pridať osvedčenie revízneho technika',
      addButtonLabel: 'Pridať osvedčenie',
      onPick: ({ slug }) =>
        form.insertListItem('certificates', {
          uuid: nanoid(),
          type: slug,
          number: '',
          renewalDate: '',
          certificate: '',
          stamp: '',
          paired: false,
        }),
    });
  }, [pickDeviceType, form]);

  /**
   * Asks the user to confirm the removal of the certificate.
   */
  const removeCertificate = useCallback(
    (index: number) => {
      confirm({
        title: 'Zmazať osvedčenie',
        content: 'Naozaj chcete zmazať osvedčenie?',
        onConfirm: () => form.removeListItem('certificates', index),
      });
    },
    [confirm, form]
  );

  return (
    <FormWrapper
      pt={24}
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText="Uložiť"
      primaryIcon={<IconCheck stroke="1.5" height={24} width={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      {form.values.certificates.map((certificate, i) => (
        <Stack key={certificate.uuid} pb={0} spacing={0}>
          <Group px={isTablet ? 20 : 40} position="apart">
            <DeviceTypeLabel size="md" deviceType={certificate.type} />
            {!certificate.paired && (
              <Button
                leftIcon={<IconTrashX stroke="1.5" />}
                variant="danger-secondary"
                onClick={() => removeCertificate(i)}
              >
                Zmazať osvedčenie
              </Button>
            )}
          </Group>
          <FormInputGroup groupTitle="">
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <TextInput
                size="lg"
                label="Číslo osvedčenia"
                placeholder="Číslo osvedčenia"
                name={`objekto-certificates.${i}.number`}
                {...form.getInputProps(`certificates.${i}.number`)}
              />
              <DateInput
                size="lg"
                label="Aktualizačná príprava"
                placeholder="Aktualizačná príprava"
                disabled={certificate.paired}
                {...form.getInputProps(`certificates.${i}.renewalDate`)}
              />
            </SimpleGrid>
            {certificate.paired && (
              <Alert color="blue" title="Spárované s HR termínom">
                Dátum obnovy a odstránenie sú chránené. Číslo a súbory môžete opraviť tlačidlom Uložiť.
                {certificate.nextDueDate ? ` Najbližší termín: ${certificate.nextDueDate}.` : ''}
                <Group mt={12}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRequalifyIndex(i);
                      requalificationForm.setValues({
                        executedOn: '',
                        certificateNumber: certificate.number,
                        certificateFileId: certificate.certificate,
                        stampFileId: certificate.stamp,
                      });
                    }}
                  >
                    Rekvalifikovať
                  </Button>
                </Group>
              </Alert>
            )}

            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <UploadFile
                label="Pečiatka"
                description="Pridajte pečiatku revízneho technika"
                {...form.getInputProps(`certificates.${i}.stamp`)}
              />
              <UploadFile
                label="Osvedčenie"
                description="Pridajte osvedčenie revízneho technika"
                {...form.getInputProps(`certificates.${i}.certificate`)}
              />
            </SimpleGrid>
          </FormInputGroup>

          <Divider color="gray.1" size={1} mt={16} mb={24} mx={isMobile ? 16 : 40} />
        </Stack>
      ))}

      {isEmpty(form.values.certificates) && (
        <Flex
          mx={isMobile ? 16 : 40}
          mt={16}
          bg="gray.0"
          py={40}
          px={isMobile ? 16 : 0}
          sx={{ borderRadius: '4px' }}
          justify="center"
          align="center"
        >
          <Stack spacing={24} maw={384} justify="center" align="center">
            <Image src="/img/no-certificates.svg" width={isMobile ? '100%' : 384} maw={384} h="auto" />
            <Stack spacing={8} justify="center" align="center" sx={{ textAlign: 'center' }}>
              <H1Medium color="gray.7">Zatial nemáte pridané žiadne osvedčenia</H1Medium>
              <Button variant="subtle" size="md" leftIcon={<IconPlus stroke="1.5" />} onClick={addCertificate}>
                Pridať osvedčenie
              </Button>
            </Stack>
          </Stack>
        </Flex>
      )}

      <Modal
        opened={requalifyIndex !== null}
        onClose={() => setRequalifyIndex(null)}
        title="Rekvalifikovať osvedčenie"
        centered
        closeOnClickOutside={!requalifying}
        closeOnEscape={!requalifying}
        closeButtonProps={{ loading: requalifying }}
      >
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            requalificationForm.onSubmit(async (values) => {
              if (requalifyIndex === null || !onRequalify) {
                return;
              }
              setRequalifying(true);
              try {
                await onRequalify(form.values.certificates[requalifyIndex], values);
                setRequalifyIndex(null);
              } finally {
                setRequalifying(false);
              }
            })(event);
          }}
        >
          <Stack>
            <DateInput label="Dátum vykonania" withAsterisk {...requalificationForm.getInputProps('executedOn')} />
            <TextInput
              label="Číslo osvedčenia"
              withAsterisk
              {...requalificationForm.getInputProps('certificateNumber')}
            />
            <UploadFile label="Osvedčenie" {...requalificationForm.getInputProps('certificateFileId')} />
            <UploadFile label="Pečiatka" {...requalificationForm.getInputProps('stampFileId')} />
            <Group position="right">
              <Button variant="subtle-gray" onClick={() => setRequalifyIndex(null)} disabled={requalifying}>
                Zrušiť
              </Button>
              <Button type="submit" loading={requalifying}>
                Potvrdiť rekvalifikáciu
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {!isEmpty(form.values.certificates) && (
        <Stack align="flex-start" pl={isTablet ? 20 : 40} pb={24}>
          <Button variant="subtle" size="md" leftIcon={<IconPlus stroke="1.5" />} onClick={addCertificate}>
            Pridať osvedčenie
          </Button>
        </Stack>
      )}
    </FormWrapper>
  );
}
